import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { getAllSites } from 'state/reducers/user.reducers';
import { getAllCps, updateCp } from 'state/reducers/cp.reducers';
import _ from 'lodash';

import { Box, Button, Card, Stack } from '@mui/material';

//style
import { StyledCard, StyledCardContent, StyledStatusBox, StyledTypographySubTitle } from '../../styled-components';

//constants
import { CpInputsForm, CPInputsFormLabels, ICp } from 'constants/cp.constants';
import { pagesEnum } from 'constants/ui.constants';
//components
import CustomInputForm from '../../../components/InputsForm/CustomInput';
import ConfirmDialog from 'components/ConfirmDialog';
//icons
import { PermissionIcon } from 'assets/icons/Permission.icon';
import { CalenderIcon } from 'assets/icons/Calender.icon';
import { EditIcon } from 'assets/icons/Edit.icon';
import { Site } from 'constants/sites.constants';
import SelectBox from 'components/SelectBox';
import NoInfo from 'components/NoInfo';
import CircularIndeterminate from 'components/Loading';
import { TabsSwitcher } from 'components/TabsSwitcher';
import ArrowBackBtn from 'components/ArrowBackBtn/arrowBackBtn';
import { formResolver } from '../../../utils/global.util';
import { setError } from 'state/reducers/cp.reducers';
import { updateSiteCpCount } from 'state/reducers/site.reducers';
import { Status } from 'constants/global.constans';

interface IFormInputs {
  dataForm: CpInputsForm;
  openProfileModal: boolean;
  setOpenProfileModal: Dispatch<SetStateAction<boolean>>;
  cp: ICp;
}

const CpInformation: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();

  const { sites } = useAppSelector((state) => state.user);

  const { cps, loading, error } = useAppSelector((state) => state.cp);
  const [formError, setFormError] = useState<{ [key: string]: boolean }>({});
  const [onEdit, setOnEdit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [cp, setCp] = useState<ICp | undefined>(undefined);
  const [statusInactive, setStatusInactive] = useState(false);
  const [assignSite, setAssignSite] = useState('');
  const [siteActive, setSiteActive] = useState(true);
  const [originalData, setOriginalData] = useState<{ idNumber?: string; assignedData: string } | undefined>(undefined);

  const errorMessage = error?.errorMessage;

  useEffect(() => {
    dispatch(getAllSites());
  }, []);

  useEffect(() => {
    if (!cps) {
      dispatch(getAllCps({}));
    }
  }, []);

  const getFullName = (firstName = '', lastName = '') => {
    if (!firstName && !lastName) return 'N/A';
    else return `${firstName || ''} ${lastName || ''}`;
  };

  const methods = useForm<IFormInputs>({
    defaultValues: {
      dataForm: {},
      cp: {}
    },
    mode: 'onTouched',
    resolver: (values) => formResolver(values, [{ key: 'idNumber', displayName: CPInputsFormLabels.ID_NUMBER }])
  });

  const {
    formState: { errors }
  } = methods;

  const idNumber = methods.getValues('dataForm.idNumber');

  useEffect(() => {
    if (cps) {
      const findCp = cps?.find((cp: ICp) => cp.id == id);
      if (!findCp) {
        setCp(undefined);
        return;
      }
      if (!findCp?.assignedSite) {
        setAssignSite('All');
      }
      setCp({ ...findCp });
      setAssignSite(findCp.assignedSite ?? 'All');
      if (findCp.assignedSite) {
        const site = sites?.find((site: Site) => site.name === findCp.assignedSite);
        setSiteActive(site?.active ?? true);
      }
      setOriginalData({ idNumber: findCp?.idNumber, assignedData: findCp.assignedSite });
      setStatusInactive(!findCp.active);
      methods.reset({
        openProfileModal: undefined,
        setOpenProfileModal: undefined,
        dataForm: {
          idNumber: (findCp?.idNumber || '') as string,
          createdAt: `${getFullName(findCp?.created_by_first_name, findCp?.created_by_last_name)} ${
            findCp?.createdAt || ''
          }` as string,
          lastLogin: findCp?.lastActivity || '',
          lastEdit: (findCp?.lastEdit || '') as string
        }
      });
    }
  }, [cps, id]);

  const onConfirmHandler = async () => {
    const updateCpData = {
      active: statusInactive
    };
    //errorMessage is error from the backend, if it exists we don't want to update the CP and we show dialog with the error message
    if (errorMessage) {
      dispatch(setError(null));
      setOpenDialog(false);
      return;
    }
    const cpId = cp?.id;
    const response = await dispatch(updateCp({ updateCpData, cpId, siteName: assignSite }));
    const isSuccess = response?.meta?.requestStatus === 'fulfilled';
    //if the api call was successful, toggle the statusInactive state and close the dialog
    if (isSuccess) {
      //we want to call updateSiteCpCount only if it site CP and not unrestricted
      if (cp && cp?.assignedSite !== 'All') {
        const siteId = sites?.find((site: Site) => site.name === cp.assignedSite)?.id;
        siteId && dispatch(updateSiteCpCount({ sitesId: siteId, isActive: updateCpData.active }));
      }
      setOpenDialog(false);
      setOnEdit(false);
    }
  };

  const formSubmitHandler: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
    const site = sites?.find((site: Site) => site.name == assignSite) || { id: null };
    const updateCpData = {
      cp_identification: data.dataForm.idNumber?.trim(),
      site_id: site?.id,
      active: !statusInactive
    };
    const cpId = cp?.id;
    await dispatch(updateCp({ updateCpData, cpId, siteName: assignSite }));
    setOriginalData({ idNumber: data.dataForm.idNumber, assignedData: assignSite });
    setOnEdit(false);
  };

  const validateField = (event: any) => {
    const { id, value }: { id: keyof ICp; value: string } = event.target;
    if (value?.length) {
      if (['name', 'idNumber'].includes(id) && value) {
        const exist = !!cps?.find((_cp: ICp) => {
          return _cp[id] === value && !_.isEqual(_cp.id, cp?.id);
        });
        if (exist) {
          setFormError({
            ...formError,
            [id]: true
          });
          return;
        }
        setFormError({
          ...formError,
          [id]: false
        });
      }
    }
  };

  const handleCancel = () => {
    methods.reset();
    originalData?.assignedData && setAssignSite(originalData.assignedData);
    setOnEdit(false);
  };

  const isSubmitDisabled =
    _.includes(_.values(formError), true) ||
    loading ||
    !idNumber?.trim() ||
    (originalData?.assignedData === assignSite && originalData?.idNumber === idNumber);

  if (loading) return <CircularIndeterminate />;
  if (!cp) return <NoInfo />;

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <FormProvider {...methods}>
        <form noValidate onChange={validateField} onSubmit={methods.handleSubmit(formSubmitHandler)}>
          <StyledCard sx={{ background: 'transparent' }}>
            <StyledCardContent>
              <Stack spacing={3}>
                <ArrowBackBtn title={'CP Info'} route={`/${pagesEnum.CP}`} />
                <Stack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
                  <Stack spacing={2} direction='row' alignItems='center'>
                    {onEdit ? (
                      <TabsSwitcher
                        onClick={() => {
                          onEdit ? setOpenDialog(true) : {};
                        }}
                        isInactive={statusInactive}
                        disabled={!onEdit}
                      />
                    ) : (
                      <StyledStatusBox isInactive={statusInactive}>
                        {statusInactive ? Status.Inactive : Status.Active}
                      </StyledStatusBox>
                    )}
                  </Stack>
                  {!onEdit && (
                    <Button
                      color='secondary'
                      data-testid='edit-button'
                      startIcon={<EditIcon />}
                      variant='outlined'
                      onClick={() => {
                        setOnEdit(true);
                      }}
                    >
                      Edit CP
                    </Button>
                  )}
                  <ConfirmDialog
                    openDialog={openDialog}
                    setOpenDialog={setOpenDialog}
                    onConfirm={onConfirmHandler}
                    dialogTitle={
                      errorMessage
                        ? 'Action Not Allowed'
                        : statusInactive
                        ? 'Convert CP to active'
                        : 'Convert CP to inactive'
                    }
                    confirmLabel='OK'
                    showCancel={!errorMessage}
                    dialogContentText={errorMessage}
                  />
                </Stack>
                <Card variant='outlined' sx={{ p: 3 }}>
                  <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
                    <CustomInputForm
                      disabled={!onEdit}
                      defaultValue={cp?.idNumber}
                      controllerName={'dataForm.idNumber'}
                      inputRegister={'dataForm.idNumber'}
                      inputId={'idNumber'}
                      label={CPInputsFormLabels.ID_NUMBER}
                      type={'text'}
                      errorMessage={
                        errors?.dataForm?.idNumber?.message ||
                        (_.get(formError, 'idNumber') ? 'Same CP id, Try other id' : '')
                      }
                      icon={<PermissionIcon />}
                    />
                    <CustomInputForm
                      disabled={true}
                      defaultValue={`${cp.created_by_first_name} ${cp.created_by_last_name} ${cp?.createdAt}`}
                      controllerName={'dataForm.createdAt'}
                      inputRegister={'dataForm.createdAt'}
                      inputId={'createdAt'}
                      label={CPInputsFormLabels.CREATED_BY}
                      type={'text'}
                      icon={<CalenderIcon />}
                    />
                  </Stack>
                </Card>
                <StyledTypographySubTitle>Assign to Site</StyledTypographySubTitle>
                <SelectBox
                  key={'sites'}
                  items={sites.map((site) => site?.name ?? '').filter((name) => name.length)}
                  values={!onEdit && !siteActive ? assignSite + ` (${Status.Inactive})` : assignSite}
                  onFilterChange={(_, filterValue: string | string[]) => {
                    setAssignSite(filterValue as string);
                  }}
                  disabledItems={sites.filter((site) => !site.active).map((site) => site.name) as string[]}
                  type={'sites'}
                  withAllOption
                  allOptionLabel='Unrestricted'
                  disabled={!onEdit}
                  inputId='site'
                />
              </Stack>
            </StyledCardContent>
          </StyledCard>
          {onEdit && (
            <Stack spacing={2} direction='row' sx={{ display: 'flex', justifyContent: 'flex-end', m: 10 }}>
              <Button color='secondary' data-testid='cancel-button' size='large' variant='text' onClick={handleCancel}>
                cancel
              </Button>
              <Button
                color='secondary'
                variant='contained'
                data-testid='save-button'
                disabled={isSubmitDisabled}
                type='submit'
                size='large'
              >
                save
              </Button>
            </Stack>
          )}
        </form>
      </FormProvider>
    </Box>
  );
};

export default CpInformation;
